import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Lock, Star } from "lucide-react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Eyebrow, MaskLines, Reveal } from "@/components/Reveal";
import { API, currency } from "@/lib/api";
import { IMG } from "@/lib/data";

const STEPS = [
  { n: "01", t: "Apply & talk", d: "Send the form below. A short call, then an invitation to the Investor Premium Club and its deal flow." },
  { n: "02", t: "Reserve & structure", d: "Choose how you invest — land, properties, or through our structure. Reserve with escrow; we handle titling, licensing and setup." },
  { n: "03", t: "Own & receive", d: "Quarterly valuations and reports on everything you hold. Distributions and exits are handled by our team on the island." },
];

const FIELD =
  "w-full border-b border-cream/25 bg-transparent py-3 text-cream outline-none transition-colors placeholder:text-cream/35 focus:border-gold";

const TICKETS = ["$50k – $100k", "$100k – $250k", "$250k – $500k", "$500k – $1M", "Above $1M"];
const HORIZONS = ["1 – 2 years", "3 – 5 years", "5 – 10 years", "10+ years"];
const EXPERIENCE = ["First private allocation", "1 – 3 previous deals", "Seasoned · 4+ deals"];

const OPTIONS = [
  {
    id: "land",
    n: "01",
    name: "Invest in land",
    tag: "Land banking",
    rate: 16,
    compound: false,
    defaultAmount: 50000,
    featured: false,
    desc: "Plotted land in South Lombok's growth corridors. Nothing to build, nothing to run — the ground does the work, modelled at 16% per year.",
    points: [
      "Pre-development corridor plots",
      "Certificates verified before entry",
      "Exit & resale assistance at term",
    ],
  },
  {
    id: "properties",
    n: "02",
    name: "Invest in properties",
    tag: "Villas & estates",
    rate: 16,
    compound: false,
    defaultAmount: 250000,
    featured: false,
    desc: "A managed villa — whole or fractional. Rental income while you hold it, appreciation when you exit, modelled at 16% per year.",
    points: [
      "Managed rental programme included",
      "Annual owner-stay allocation",
      "Full stewardship by our team",
    ],
  },
  {
    id: "through-us",
    n: "03",
    name: "Investor Priviledge Club",
    tag: "By acceptance only · highest target",
    rate: 24,
    compound: true,
    defaultAmount: 100000,
    featured: true,
    byAcceptance: true,
    desc: "A circle of selected businessmen, brought together by a shared interest in private allocation. Your capital enters Pachamama's investment vehicle rather than a single named asset: we acquire at pre-market prices, build, manage and exit across the club's full allocation — modelled at 24% per year. Access is by acceptance: every application is reviewed personally.",
    points: [
      "Membership by acceptance — applications reviewed one by one",
      "Capital rides the club's full allocation, pre-market entry",
      "Acquisition, titling, build & management handled",
      "Priority access to off-market inventory",
    ],
    privileges: [
      "A 10-day stay in one of our properties every year — our token of appreciation",
      "A yearly sit-down to navigate through the numbers together",
      "Private circle: introductions, co-investments and first look at new allocations",
    ],
  },
];

const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="bg-ink px-3 py-2 font-mono text-[11px] text-cream">
      <p className="text-gold">Year {label}</p>
      {payload.map((p, i) => (
        <p key={i} className="mt-0.5">
          {p.name}: {currency(p.value)}
        </p>
      ))}
    </div>
  ) : null;

function ProfitCalculator({ opt }) {
  const [amount, setAmount] = useState(opt.defaultAmount);
  const [years, setYears] = useState(5);

  const series = useMemo(
    () =>
      Array.from({ length: years }, (_, i) => {
        const y = i + 1;
        const value = opt.compound
          ? amount * Math.pow(1 + opt.rate / 100, y)
          : amount * (1 + (opt.rate / 100) * y);
        return { year: y, Value: Math.round(value) };
      }),
    [amount, years, opt.rate, opt.compound]
  );

  const finalValue = series[series.length - 1]?.Value ?? amount;
  const profit = finalValue - amount;
  const clamp = (v) => Math.max(10000, Math.min(5000000, Number(v) || 10000));

  return (
    <div className="space-y-8" data-testid={`option-calculator-${opt.id}`}>
      <div>
        <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">
          <span>Amount to invest</span>
          <span className="text-gold">USD</span>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-cream/20 bg-ink/50 px-5 py-3.5">
          <span className="font-mono text-lg text-cream/50">$</span>
          <input
            type="number"
            min={10000}
            max={5000000}
            step={5000}
            value={amount}
            onChange={(e) => setAmount(clamp(e.target.value))}
            data-testid={`option-amount-input-${opt.id}`}
            aria-label={`Amount to invest — ${opt.name}`}
            className="w-full bg-transparent font-mono text-lg text-cream outline-none"
          />
        </div>
        <input
          type="range"
          min={10000}
          max={1000000}
          step={5000}
          value={Math.min(amount, 1000000)}
          onChange={(e) => setAmount(Number(e.target.value))}
          data-testid={`option-amount-slider-${opt.id}`}
          aria-label={`Amount slider — ${opt.name}`}
          style={{ "--fill": `${((Math.min(amount, 1000000) - 10000) / 990000) * 100}%` }}
          className="mt-4"
        />
      </div>

      <div>
        <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">
          <span>Holding period</span>
          <span className="text-gold" data-testid={`option-years-display-${opt.id}`}>
            {years} {years === 1 ? "year" : "years"}
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={5}
          step={1}
          value={years}
          onChange={(e) => setYears(Number(e.target.value))}
          data-testid={`option-years-slider-${opt.id}`}
          aria-label={`Holding period — ${opt.name}`}
          style={{ "--fill": `${((years - 1) / 4) * 100}%` }}
          className="mt-4"
        />
      </div>

      <div className="grid grid-cols-2 gap-5 rounded-xl bg-ink/50 p-5">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">
            Projected value
          </p>
          <p
            className="mt-2 font-display text-2xl text-gold md:text-3xl"
            data-testid={`option-projected-${opt.id}`}
          >
            {currency(finalValue)}
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">
            Your profit
          </p>
          <p
            className="mt-2 font-display text-2xl text-cream md:text-3xl"
            data-testid={`option-profit-${opt.id}`}
          >
            +{currency(profit)}
          </p>
        </div>
      </div>

      <div className="h-36" data-testid={`option-chart-${opt.id}`}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${opt.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D4A359" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#D4A359" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="year"
              tick={{ fill: "#859389", fontSize: 9, fontFamily: "JetBrains Mono" }}
              tickFormatter={(y) => `Y${y}`}
              axisLine={false}
              tickLine={false}
              minTickGap={18}
            />
            <YAxis hide domain={[0, "auto"]} />
            <Tooltip content={<ChartTip />} cursor={{ stroke: "rgba(212,163,89,0.4)" }} />
            <Area type="monotone" dataKey="Value" stroke="#D4A359" strokeWidth={2} fill={`url(#grad-${opt.id})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/40">
        Modelled at {opt.rate}% per year,{" "}
        {opt.compound ? "compounded annually" : "simple, non-compounded"} —
        illustrative, not a guarantee
      </p>
    </div>
  );
}

function OptionCard({ opt }) {
  return (
    <div
      className={`grain relative overflow-hidden rounded-2xl border p-8 md:p-12 ${
        opt.featured
          ? "border-gold/60 bg-palm shadow-[0_0_60px_rgba(212,163,89,0.12)]"
          : "border-cream/10 bg-palm/40"
      }`}
      data-testid={`option-card-${opt.id}`}
    >
      <div className="relative z-10 grid gap-12 lg:grid-cols-2">
        <div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="font-mono text-sm text-terracotta">{opt.n}</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
              {opt.tag}
            </span>
          </div>
          <h3 className="mt-5 font-display text-3xl md:text-5xl">{opt.name}</h3>
          {opt.byAcceptance && (
            <p
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"
              data-testid={`option-acceptance-${opt.id}`}
            >
              <Lock className="h-3 w-3" /> Access subject to acceptance
            </p>
          )}
          <p
            className={`mt-4 font-display text-2xl ${opt.featured ? "text-gold" : "text-cream"}`}
            data-testid={`option-rate-${opt.id}`}
          >
            {opt.rate}% per year
          </p>
          <p className="mt-6 leading-relaxed text-cream/70">{opt.desc}</p>
          <ul className="mt-8 space-y-3">
            {opt.points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-cream/75">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                {p}
              </li>
            ))}
          </ul>
          {opt.privileges && (
            <div className="mt-8 rounded-xl border border-gold/30 bg-ink/40 p-6" data-testid={`option-privileges-${opt.id}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Member privileges</p>
              <ul className="mt-4 space-y-3">
                {opt.privileges.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-cream/85">
                    <Star className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <ProfitCalculator opt={opt} />
      </div>
    </div>
  );
}

function JoinForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    tier: "",
    message: "",
    ticket_size: "",
    horizon: "",
    referral: "",
    experience: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const isClub = form.tier === "Investor Priviledge Club";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success("Application received — welcome to the shortlist.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-2xl border border-gold/40 bg-palm p-10 text-center"
        data-testid="join-form-success"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold text-gold">
          <Check className="h-6 w-6" />
        </span>
        <h3 className="mt-6 font-display text-3xl text-cream">You're on the list.</h3>
        <p className="mt-3 text-cream/65">
          Our investment director reviews every application personally and will
          reach out within two business days.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-cream/10 bg-palm p-8 md:p-10"
      data-testid="join-form"
    >
      <h3 className="font-display text-3xl text-cream">Request an invitation</h3>
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <input
          required
          placeholder="Full name"
          className={FIELD}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="join-name-input"
        />
        <input
          required
          type="email"
          placeholder="Email address"
          className={FIELD}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="join-email-input"
        />
        <input
          placeholder="Phone / WhatsApp"
          className={FIELD}
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="join-phone-input"
        />
        <select
          className={`${FIELD} [&>option]:bg-ink`}
          value={form.tier}
          onChange={(e) => setForm({ ...form, tier: e.target.value })}
          data-testid="join-tier-select"
        >
          <option value="">Interested in…</option>
          {OPTIONS.map((o) => (
            <option key={o.id} value={o.name}>
              {o.name} · {o.rate}% p.a.
            </option>
          ))}
          <option value="undecided">Not sure yet</option>
        </select>
      </div>
      {isClub && (
        <div className="mt-8 rounded-xl border border-gold/30 bg-ink/40 p-6" data-testid="join-vetting">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
            Investor Priviledge Club · a few questions before the call
          </p>
          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <select
              required
              className={`${FIELD} [&>option]:bg-ink`}
              value={form.ticket_size}
              onChange={(e) => setForm({ ...form, ticket_size: e.target.value })}
              data-testid="join-ticket-select"
            >
              <option value="">Intended ticket size…</option>
              {TICKETS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              required
              className={`${FIELD} [&>option]:bg-ink`}
              value={form.horizon}
              onChange={(e) => setForm({ ...form, horizon: e.target.value })}
              data-testid="join-horizon-select"
            >
              <option value="">Investment horizon…</option>
              {HORIZONS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <select
              className={`${FIELD} [&>option]:bg-ink`}
              value={form.experience}
              onChange={(e) => setForm({ ...form, experience: e.target.value })}
              data-testid="join-experience-select"
            >
              <option value="">Experience with private allocations…</option>
              {EXPERIENCE.map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
            <input
              placeholder="Referred by (member or partner, optional)"
              className={FIELD}
              value={form.referral}
              onChange={(e) => setForm({ ...form, referral: e.target.value })}
              data-testid="join-referral-input"
            />
          </div>
        </div>
      )}
      <textarea
        rows={3}
        placeholder="Anything we should know?"
        className={`${FIELD} mt-6 resize-none`}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        data-testid="join-message-input"
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="join-form-submit"
        className="group mt-8 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-cream disabled:opacity-50"
      >
        {isClub ? "Apply for acceptance" : "Apply to the club"}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}

function Hero() {
  return (
    <section className="relative flex min-h-[88svh] items-end overflow-hidden bg-ink">
      <img
        src={IMG.turquoise}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-20 pt-40 md:px-10 md:pb-28">
        <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
          The Investor Premium Club
        </p>
        <h1 className="font-display text-[clamp(2.6rem,7.5vw,6.5rem)] leading-[0.98] text-cream">
          <MaskLines
            delay={0.3}
            lines={[<>Capital, planted</>, <em className="text-gold">in volcanic soil.</em>]}
          />
        </h1>
        <p className="mt-8 max-w-lg leading-relaxed text-cream/75">
          Three ways in — invest in land, invest in properties, or invest
          through our structure. Target returns published up front,
          distributions paid quarterly.
        </p>
      </div>
    </section>
  );
}

export default function InvestorClub() {
  return (
    <main>
      <Hero />

      <section className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-36">
        <Reveal>
          <Eyebrow>How it works</Eyebrow>
        </Reveal>
        <div className="mt-12 grid gap-10 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="border-t-2 border-terracotta pt-6" data-testid={`step-${s.n}`}>
                <span className="font-mono text-sm text-terracotta">{s.n}</span>
                <h3 className="mt-3 font-display text-2xl md:text-3xl">{s.t}</h3>
                <p className="mt-3 leading-relaxed text-moss">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="grain relative bg-ink py-24 text-cream md:py-36">
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow light>Three ways in</Eyebrow>
              <h2 className="mt-5 font-display text-4xl leading-[1.05] md:text-6xl">
                Land, properties, <em className="text-gold">or the Investor Priviledge Club</em>
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-cream/60">
              Type an amount and watch the profit build year by year. Every
              entry is tailored in the invitation call — minimums are where
              the conversation starts.
            </p>
          </Reveal>

          <div className="mt-14 space-y-8">
            {OPTIONS.map((opt, i) => (
              <Reveal key={opt.id} delay={i * 0.06}>
                <OptionCard opt={opt} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="join" className="mx-auto max-w-3xl px-6 pb-24 md:pb-36">
        <Reveal>
          <JoinForm />
        </Reveal>
        <p className="mt-8 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-sage">
          All targets are illustrative, based on historical public market data.
          Property investment carries risk, including loss of capital.
        </p>
      </section>
    </main>
  );
}
